/**
 * Runs the AP-731 cascade for one reader action (§1). Called from every path
 * that writes a measurement — never from an effect, so load, refetch and save
 * cannot reach it (R4).
 *
 * The snapshot comes from `before`, the report as it was on entry, so it is
 * genuinely pre-action. It is derived by the same resolver the worksheet renders
 * from, so the engine and the UI can never disagree about what was diverged.
 */

import type { Measurement } from '@/models/report';
import type { AIMeasurement } from '@/models/aiScan';
import type { MeasurementGroup } from '@/models/template';
import { resolveMeasurementRows, selectAIVariants } from '../Measurements';
import type { DivergenceState } from './divergence';
import { recalculate, type EngineRow } from './recalculate';

/** The three reader actions that write a measurement value (R4). */
export type CascadeAction = 'edit' | 'delete' | 'converge';

export interface ApplyCascadeArgs {
  /** Measurements as they were before the reader's change. */
  before: Measurement[];
  /** Measurements with the reader's own change already applied. */
  after: Measurement[];
  measurementGroups: MeasurementGroup[];
  aiMeasurements: AIMeasurement[];
  sex?: string | null;
  /** canonicalMeasurement(s) the reader's action wrote directly. */
  target: string | string[];
  /** Which of the three write actions produced `after`. */
  action: CascadeAction;
}

export function applyCascade({
  before,
  after,
  measurementGroups,
  aiMeasurements,
  sex,
  target,
  action,
}: ApplyCascadeArgs): Measurement[] {
  const labels = measurementGroups.flatMap((g) => g.measurementLabels ?? []);
  if (!labels.length) return after;

  const targets = Array.isArray(target) ? target : [target];
  const targetLabels = targets.map((t) =>
    labels.find((l) => l.canonicalMeasurement === t)
  );
  // Divergence as of immediately before the action, so a row whose input
  // just changed is recalculated rather than read as diverged.
  const priorRows = resolveMeasurementRows({
    measurementGroups,
    measurements: before,
    aiMeasurements,
    sex,
  });
  const snapshot = new Map<string, DivergenceState>();
  labels.forEach((ml) => {
    if (!ml.canonicalMeasurementKey || !ml.canonicalMeasurement) return;
    snapshot.set(
      ml.canonicalMeasurementKey,
      priorRows.get(ml.canonicalMeasurement)?.divergence ?? null
    );
  });

  const rowByCanonical = new Map<string, Measurement>();
  after.forEach((m) => {
    if (m.canonicalMeasurement) rowByCanonical.set(m.canonicalMeasurement, m);
  });
  const aiRoles = selectAIVariants(aiMeasurements);

  const rows: EngineRow[] = [];
  const slugToCanonical = new Map<string, string>();
  labels.forEach((ml) => {
    const canonical = ml.canonicalMeasurement;
    const slug = ml.canonicalMeasurementKey;
    if (!canonical || !slug) return;
    const row = rowByCanonical.get(canonical);
    slugToCanonical.set(slug, canonical);
    rows.push({
      slug,
      value: row?.value ?? null,
      valueSource: row?.valueSource ?? null,
      hasHumanAnnotation: !!row?.annotation,
      aiValue: aiRoles.get(canonical)?.display.value ?? null,
      formula:
        ml.type === 'DERIVED' || ml.type === 'INDEXED' ? ml.formula : null,
      precision: ml.precision ?? 2,
    });
  });

  const writes = recalculate({
    rows,
    snapshot,
    clearUnresolvable: action === 'delete',
    // R5 — never write back to a row the reader acted on.
    protectedSlugs: new Set(
      targetLabels.flatMap((ml) =>
        ml?.canonicalMeasurementKey ? [ml.canonicalMeasurementKey] : []
      )
    ),
  });
  if (!writes.size) return after;

  // R6 — an engine write is marked manual so it outranks the row's own AI
  // reading; without it the write would never display.
  const byCanonical = new Map<string, number | null>();
  writes.forEach((value, slug) => {
    const canonical = slugToCanonical.get(slug);
    if (canonical) byCanonical.set(canonical, value);
  });

  const now = new Date().toISOString();
  const seen = new Set<string>();
  const next = after.map((m) => {
    const canonical = m.canonicalMeasurement;
    if (!canonical || !byCanonical.has(canonical)) return m;
    seen.add(canonical);
    const value = byCanonical.get(canonical) ?? null;
    return {
      ...m,
      value,
      // A clear stays MANUAL too, or the row's own AI reading would reappear.
      valueSource: 'MANUAL' as const,
      updatedAt: now,
    };
  });

  // A computed row the report has never carried yet still needs its result.
  byCanonical.forEach((value, canonical) => {
    if (seen.has(canonical) || value === null) return;
    const ml = labels.find((l) => l.canonicalMeasurement === canonical);
    next.push({
      canonicalMeasurement: canonical,
      canonicalMeasurementKey: ml?.canonicalMeasurementKey ?? null,
      value,
      valueSource: 'MANUAL',
      unit: ml?.unit ?? '',
      createdAt: now,
      updatedAt: now,
      annotation: null,
    });
  });

  return next;
}
